/**
 * Dropbox OAuth Authentication
 *
 * OAuth authentication using expo-auth-session for Dropbox access
 */

import * as AuthSession from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import * as SecureStore from 'expo-secure-store';
import { reportError } from '@/lib/errors';
import {
  getDropboxConfig,
  DROPBOX_DISCOVERY,
  DROPBOX_TOKEN_ACCESS_TYPE,
} from './config';
import type { DropboxTokens } from './types';

// Complete any pending auth session on web
WebBrowser.maybeCompleteAuthSession();

// Storage key for Dropbox tokens
const DROPBOX_TOKENS_KEY = 'inverio-dropbox-tokens';

/**
 * Hook for Dropbox OAuth authentication
 *
 * Uses expo-auth-session to handle the OAuth flow with PKCE
 *
 * @returns OAuth request state and functions
 */
export function useDropboxAuth() {
  const config = getDropboxConfig();

  // Create redirect URI using Expo's helper
  // Uses the app scheme defined in app.json
  const redirectUri = AuthSession.makeRedirectUri({
    scheme: 'inverio',
    path: 'oauth/dropbox',
  });

  // Create auth request with PKCE
  // Dropbox uses code flow with PKCE for mobile apps
  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: config.appKey,
      redirectUri,
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
      extraParams: {
        token_access_type: DROPBOX_TOKEN_ACCESS_TYPE,
      },
    },
    DROPBOX_DISCOVERY
  );

  return {
    request,
    response,
    promptAsync,
    redirectUri,
    isConfigured: !!config.appKey,
  };
}

/**
 * Save Dropbox tokens to secure storage
 */
export async function saveDropboxTokens(tokens: DropboxTokens): Promise<void> {
  await SecureStore.setItemAsync(DROPBOX_TOKENS_KEY, JSON.stringify(tokens));
}

/**
 * Get Dropbox tokens from secure storage
 */
export async function getDropboxTokens(): Promise<DropboxTokens | null> {
  const data = await SecureStore.getItemAsync(DROPBOX_TOKENS_KEY);
  if (!data) return null;

  try {
    return JSON.parse(data) as DropboxTokens;
  } catch {
    return null;
  }
}

/**
 * Clear Dropbox tokens from secure storage
 */
export async function clearDropboxTokens(): Promise<void> {
  await SecureStore.deleteItemAsync(DROPBOX_TOKENS_KEY);
}

/**
 * Exchange authorization code for access tokens
 *
 * @param code - Authorization code from OAuth flow
 * @param codeVerifier - PKCE code verifier
 * @param redirectUri - Redirect URI used in the request
 * @returns Dropbox tokens
 */
export async function exchangeDropboxCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string
): Promise<DropboxTokens> {
  const config = getDropboxConfig();

  if (!config.appKey) {
    throw new Error('Dropbox app key not configured');
  }

  const response = await fetch(DROPBOX_DISCOVERY.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      code,
      grant_type: 'authorization_code',
      client_id: config.appKey,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    }).toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed: ${errorText}`);
  }

  const data = await response.json();

  const tokens: DropboxTokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: data.expires_in
      ? Date.now() + data.expires_in * 1000
      : undefined,
    accountId: data.account_id,
    uid: data.uid,
  };

  // Save tokens for later use
  await saveDropboxTokens(tokens);

  return tokens;
}

/**
 * Refresh an expired access token
 *
 * @param refreshToken - The refresh token
 * @returns New Dropbox tokens
 */
export async function refreshDropboxToken(
  refreshToken: string
): Promise<DropboxTokens> {
  const config = getDropboxConfig();

  if (!config.appKey) {
    throw new Error('Dropbox app key not configured');
  }

  const response = await fetch(DROPBOX_DISCOVERY.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: config.appKey,
    }).toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token refresh failed: ${errorText}`);
  }

  const data = await response.json();

  // Get existing tokens to preserve account info
  const existingTokens = await getDropboxTokens();

  const tokens: DropboxTokens = {
    accessToken: data.access_token,
    // Dropbox doesn't return a new refresh token on refresh
    refreshToken: existingTokens?.refreshToken || refreshToken,
    expiresAt: data.expires_in
      ? Date.now() + data.expires_in * 1000
      : undefined,
    accountId: existingTokens?.accountId,
    uid: existingTokens?.uid,
  };

  await saveDropboxTokens(tokens);

  return tokens;
}

/**
 * Check if tokens are still valid
 *
 * @param tokens - Dropbox tokens to check
 * @returns true if tokens are valid, false otherwise
 */
export function isDropboxTokenValid(tokens: DropboxTokens | null): boolean {
  if (!tokens) return false;
  if (!tokens.accessToken) return false;
  if (!tokens.expiresAt) return true; // No expiry info, assume valid

  // Add 1 minute buffer to avoid edge cases
  return Date.now() < tokens.expiresAt - 60000;
}

/**
 * Get valid tokens, refreshing if necessary
 *
 * @returns Valid Dropbox tokens or null if unavailable
 */
export async function getValidDropboxTokens(): Promise<DropboxTokens | null> {
  const tokens = await getDropboxTokens();

  if (!tokens) return null;

  if (isDropboxTokenValid(tokens)) {
    return tokens;
  }

  // Token expired, try to refresh
  if (tokens.refreshToken) {
    try {
      return await refreshDropboxToken(tokens.refreshToken);
    } catch (error) {
      reportError(error, { context: 'DropboxAuth.getValidAccessToken.refresh', level: 'warning' });
      // Clear invalid tokens
      await clearDropboxTokens();
      return null;
    }
  }

  // No refresh token, clear and return null
  await clearDropboxTokens();
  return null;
}

/**
 * Revoke Dropbox access tokens
 */
export async function revokeDropboxTokens(): Promise<void> {
  const tokens = await getDropboxTokens();

  if (tokens?.accessToken) {
    try {
      // Revoke the token with Dropbox
      // Dropbox revocation endpoint requires Bearer token auth
      await fetch(DROPBOX_DISCOVERY.revocationEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      });
    } catch (error) {
      reportError(error, { context: 'DropboxAuth.revokeDropboxTokens', level: 'warning', silent: true });
    }
  }

  // Clear local tokens regardless of revocation result
  await clearDropboxTokens();
}
